import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useI18n } from '../i18n';
import { setActiveSlot } from '../lib/activeSlot';
import { useAuth } from './AuthProvider';
import { useIsAdmin } from './useIsAdmin';
import { useIsSuperadmin } from './useIsSuperadmin';

/**
 * Gardes de routes (multi-login XFILE-14).
 *
 * `PrivateRoute` — 3 états, dans l'ordre : (1) chargement → spinner ; (2) panne
 * réseau → écran « réessayer » (JAMAIS de redirection vers /login sur un 5xx) ;
 * (3) non authentifié → /login (en mémorisant la page visée).
 *
 * `AdminRoute` / `SuperadminRoute` — gardes par RÔLE des pages d'administration
 * (détail sur chacune plus bas). La première admet `admin` ET `superadmin`, la
 * seconde le seul `superadmin`.
 *
 * `LegacySlotRedirect` — rétro-compat des anciens liens `/u/:slot/*` : pose le
 * slot puis redirige vers l'URL propre équivalente (le slot est local au navigateur,
 * hors de l'URL → liens partageables).
 */

/** Écran d'état plein cadre (chargement / erreur). Réutilisé comme repli `Suspense`. */
export function CenteredStatus({
  role,
  children,
}: {
  role: 'status' | 'alert';
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-surface px-4 text-center text-muted">
      <div role={role}>{children}</div>
    </div>
  );
}

export function PrivateRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { t } = useI18n();
  const location = useLocation();
  const {
    user,
    loading,
    accountsLoading,
    accountsError,
    networkError,
    refreshAccounts,
    refreshMe,
  } = useAuth();

  if (accountsLoading || loading) {
    return <CenteredStatus role="status">{t('auth.checkingSession')}</CenteredStatus>;
  }

  if (networkError || accountsError) {
    return (
      <CenteredStatus role="alert">
        <p className="mb-4 text-fg">{t('auth.networkErrorTitle')}</p>
        <button
          type="button"
          onClick={() => {
            void refreshAccounts();
            void refreshMe();
          }}
          className="rounded-md bg-brand-strong px-3 py-2 text-sm font-medium text-brand-fg transition-colors hover:bg-brand-strong-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand motion-reduce:transition-none"
        >
          {t('auth.retry')}
        </button>
      </CenteredStatus>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * `SuperadminRoute` — défense en profondeur pour la CONSOLE superadmin (`/admin`,
 * XFILE-07/-09) : un non-superadmin (lecteur ou `admin`) qui atteint l'URL directement
 * est REDIRIGÉ vers l'accueil (les affordances de console sont déjà masquées via
 * `useIsSuperadmin`, et le serveur renvoie 403 ; ceci ferme aussi l'accès par URL).
 * Suppose l'auth déjà garantie en amont (`PrivateRoute` parent).
 */
export function SuperadminRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const isSuperadmin = useIsSuperadmin();
  if (!isSuperadmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

/**
 * `AdminRoute` — défense en profondeur pour les pages d'ADMINISTRATION ouvertes aux DEUX rôles
 * élevés (`admin` et `superadmin`) : aujourd'hui la corbeille `/trash` (XFILE-72). Un rôle `user`
 * qui atteint l'URL directement est REDIRIGÉ vers l'accueil (le lien est déjà masqué via
 * `useIsAdmin`, et le serveur répond 403 ; ceci ferme aussi l'accès par URL). Suppose l'auth déjà
 * garantie en amont (`PrivateRoute` parent).
 *
 * DISTINCTE de `SuperadminRoute`, et les deux doivent le rester : la console `/admin` est réservée
 * au superadmin STRICT, tandis que l'administration courante du dépôt est ouverte à l'`admin`. Les
 * confondre ouvrirait la console ou fermerait la corbeille à la moitié de ses ayants droit.
 */
export function AdminRoute({ children }: { children: React.ReactNode }): React.JSX.Element {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export function LegacySlotRedirect(): React.JSX.Element {
  const { slot } = useParams();
  const location = useLocation();
  // Idempotent : pose le slot demandé avant de rediriger vers l'URL propre.
  if (slot !== undefined && /^\d+$/.test(slot)) {
    setActiveSlot(Number.parseInt(slot, 10));
  }
  const rest = location.pathname.replace(/^\/u\/\d+/, '') || '/';
  return <Navigate replace to={`${rest}${location.search}`} />;
}
